import { FormEvent, useEffect, useMemo, useState } from "react";
import { formatDate } from "../lib/format";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { catalogApi, type ProductSummary } from "../api/catalog";
import { adminApi, type Company } from "../api/admin";
import {
  forecastsApi,
  periodLabel,
  type Forecast,
  type ForecastLine,
  type ForecastStatus,
} from "../api/forecasts";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import { Select } from "../components/Select";
import { COUNTRIES, flagEmoji } from "../i18n/countries";

const QUARTERS = [1, 2, 3, 4];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/**
 * Forecasts landing — design-system styled with KPI strip per status, a
 * status filter chip row, customer filter for SALES, and a Create flow that
 * asks SALES which of their assigned customers the forecast is for
 * (CUST_MANAGER pre-fills with own company). Empty state has a friendly
 * CTA so a first-time visitor understands what to do.
 */
export default function ForecastsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { state, hasPermission } = useAuth();
  const canWrite = hasPermission("section:forecasts:write");
  const canFull = hasPermission("section:forecasts:full");
  const [params] = useSearchParams();

  const [items, setItems] = useState<Forecast[] | null>(null);
  const [editing, setEditing] = useState<Forecast | null>(null);
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ForecastStatus | "ALL">("ALL");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [salesCompanies, setSalesCompanies] = useState<Company[]>([]);

  const isInternal = state.status === "authenticated" && state.user.internal;

  async function load() {
    try {
      setItems(await forecastsApi.list({ companyId: companyFilter || undefined }));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }
  useEffect(() => { void load(); }, [companyFilter]);

  useEffect(() => {
    if (!isInternal) return;
    void adminApi
      .listCompanies(0, 200)
      .then((p) => setSalesCompanies(p.content.filter(Boolean)))
      .catch(() => setSalesCompanies([]));
  }, [isInternal]);

  useEffect(() => {
    if (canWrite && params.get("new") === "1") setCreating(true);
  }, [params, canWrite]);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (statusFilter === "ALL") return items;
    return items.filter((f) => f.status === statusFilter);
  }, [items, statusFilter]);

  const stats = useMemo(() => {
    const list = items ?? [];
    return {
      total: list.length,
      draft: list.filter((f) => f.status === "DRAFT").length,
      submitted: list.filter((f) => f.status === "SUBMITTED").length,
    };
  }, [items]);

  if (state.status !== "authenticated") return null;
  const canManage = canWrite && hasPermission("forecasts:manage");
  const ownCompanyId = state.user.companyId;
  const canCreateNow = canManage && (isInternal ? salesCompanies.length > 0 : !!ownCompanyId);

  async function submit(f: Forecast) {
    if (!canManage) return;
    if (!(await confirm({ message: t("forecasts.confirmSubmit", { period: periodLabel(f), defaultValue: `Submit ${periodLabel(f)}?` }) }))) return;
    try {
      await forecastsApi.submit(f.id);
      toast.success(t("forecasts.toast.submitted", "Submitted."));
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function remove(f: Forecast) {
    if (!canFull || !canManage) return;
    if (!(await confirm({ message: t("forecasts.confirmDelete", { period: periodLabel(f), defaultValue: `Delete ${periodLabel(f)}?` }), danger: true }))) return;
    try {
      await forecastsApi.delete(f.id);
      toast.success(t("forecasts.toast.deleted", "Deleted."));
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  const initialCreateCompanyId = isInternal
    ? (params.get("companyId") ?? salesCompanies[0]?.id ?? "")
    : (ownCompanyId ?? "");

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{t("forecasts.title", "Forecasts")}</h1>
          <div className="sub">
            {isInternal
              ? t("forecasts.subtitle.internal", "Demand forecasts for your assigned customers. Submitted forecasts feed into Bankom production planning.")
              : t("forecasts.subtitle.customer", "Forward-looking demand from your team. Submit forecasts so Bankom can plan production.")}
          </div>
        </div>
        {canCreateNow && (
          <button type="button" onClick={() => setCreating(true)} className="btn primary">
            {t("forecasts.newForecast", "+ New forecast")}
          </button>
        )}
      </div>

      <div style={kpiGrid}>
        <KpiTile label={t("forecasts.kpi.total", "Total")}         value={stats.total}     accent="var(--brand-primary-600)" bg="var(--info-bg)" />
        <KpiTile label={t("forecasts.kpi.draft", "Draft")}         value={stats.draft}     accent="var(--ink-3)" bg="var(--surface-muted)" />
        <KpiTile label={t("forecasts.kpi.submitted", "Submitted")} value={stats.submitted} accent="var(--info)" bg="var(--info-bg)" />
      </div>

      <div style={chipBar}>
        <Chip label={t("forecasts.chip.all", "All {{count}}", { count: stats.total })}             active={statusFilter === "ALL"}        onClick={() => setStatusFilter("ALL")} />
        <Chip label={t("forecasts.chip.draft", "Draft {{count}}", { count: stats.draft })}          active={statusFilter === "DRAFT"}      onClick={() => setStatusFilter(statusFilter === "DRAFT" ? "ALL" : "DRAFT")} />
        <Chip label={t("forecasts.chip.submitted", "Submitted {{count}}", { count: stats.submitted })} active={statusFilter === "SUBMITTED"} onClick={() => setStatusFilter(statusFilter === "SUBMITTED" ? "ALL" : "SUBMITTED")} />
        {isInternal && salesCompanies.length > 0 && (
          <Select<string>
            value={companyFilter}
            onChange={setCompanyFilter}
            options={[
              { value: "", label: t("forecasts.filter.allCustomers", "All customers") },
              ...salesCompanies.map((c) => ({
                value: c.id,
                label: `${c.country ? `${flagEmoji(c.country)} ` : ""}${c.name}`,
              })),
            ]}
            style={{ marginLeft: "auto", minWidth: 200 }}
          />
        )}
      </div>

      {!items ? (
        <p style={{ color: "var(--ink-3)" }}>{t("forecasts.loading", "Loading…")}</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          isFiltered={stats.total > 0}
          canCreate={canCreateNow}
          onCreate={() => setCreating(true)}
          isInternal={isInternal}
        />
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={tbl} className="list-card">
            <thead>
              <tr>
                <th style={th}>{t("forecasts.col.period", "Period")}</th>
                <th style={th}>{t("forecasts.col.company", "Customer")}</th>
                <th style={th}>{t("forecasts.col.lines", "Lines")}</th>
                <th style={th}>{t("forecasts.col.status", "Status")}</th>
                <th style={th}>{t("forecasts.col.submitted", "Submitted")}</th>
                <th style={th} aria-label={t("forecasts.col.actions", "Actions")} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => (
                <tr key={f.id}>
                  <td style={td} data-label={t("forecasts.col.period", "Period")}><b style={{ color: "var(--ink)" }}>{periodLabel(f)}</b></td>
                  <td style={td} data-label={t("forecasts.col.company", "Customer")}>{f.companyName}</td>
                  <td style={td} data-label={t("forecasts.col.lines", "Lines")}>{f.forecastLines.length}</td>
                  <td style={td} data-label={t("forecasts.col.status", "Status")}><StatusBadge status={f.status} /></td>
                  <td style={td} data-label={t("forecasts.col.submitted", "Submitted")}>{f.submittedAt ? formatDate(f.submittedAt) : <span style={{ color: "var(--ink-4)" }}>—</span>}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }} className="card-actions">
                    {canManage && f.status === "DRAFT" ? (
                      <>
                        <button type="button" onClick={() => setEditing(f)} style={linkBtn}>{t("forecasts.action.edit", "Edit")}</button>
                        <button type="button" onClick={() => submit(f)}    style={linkBtn}>{t("forecasts.action.submit", "Submit")}</button>
                        <button type="button" onClick={() => remove(f)} disabled={!canFull} style={dangerLink}>{t("forecasts.action.delete", "Delete")}</button>
                      </>
                    ) : (
                      <button type="button" onClick={() => setEditing(f)} style={linkBtn}>{t("forecasts.action.view", "View")}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && canManage && (
        <CreateModal
          initialCompanyId={initialCreateCompanyId}
          companies={isInternal ? salesCompanies : []}
          isInternal={isInternal}
          onClose={() => setCreating(false)}
          onSaved={async () => { setCreating(false); await load(); }}
        />
      )}
      {editing && (
        <EditModal
          forecast={editing}
          canEdit={canManage && editing.status === "DRAFT"}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function EmptyState({
  isFiltered, canCreate, onCreate, isInternal,
}: {
  isFiltered: boolean;
  canCreate: boolean;
  onCreate: () => void;
  isInternal: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div style={emptyCard}>
      <div style={{ fontSize: 28, marginBottom: 4 }}>📈</div>
      <b style={{ color: "var(--ink-2)" }}>
        {isFiltered
          ? t("forecasts.empty.filtered", "No forecasts match the current filter.")
          : isInternal
            ? t("forecasts.empty.internal.title", "No forecasts have been entered for your customers yet.")
            : t("forecasts.empty.customer.title", "No forecasts on file yet.")}
      </b>
      <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4, maxWidth: 480 }}>
        {isInternal
          ? t("forecasts.empty.internal.body", "Forecasts capture forward demand per customer per quarter or month. Submit them so Bankom production planning can size raw-material orders.")
          : t("forecasts.empty.customer.body", "A forecast is your forward demand for a quarter or month. Submitting one lets Bankom plan production capacity for your needs.")}
      </div>
      {canCreate && (
        <button type="button" onClick={onCreate} className="btn primary" style={{ marginTop: 12 }}>
          {t("forecasts.empty.cta", "+ Create your first forecast")}
        </button>
      )}
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? "var(--brand-primary)" : "var(--surface)",
        color: active ? "var(--on-primary)" : "var(--ink-2)",
        border: `1px solid ${active ? "var(--brand-primary)" : "var(--border)"}`,
        padding: "5px 11px",
        borderRadius: 999,
        fontSize: 12.5,
        cursor: "pointer",
        fontWeight: 500,
      }}
    >
      {label}
    </button>
  );
}

function KpiTile({ label, value, accent, bg }: { label: string; value: number; accent: string; bg: string }) {
  return (
    <div className="card" style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: accent, boxShadow: `0 0 0 4px ${bg}` }} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

function CreateModal({
  initialCompanyId, companies, isInternal, onClose, onSaved,
}: {
  initialCompanyId: string;
  companies: Company[];
  isInternal: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [companyId, setCompanyId] = useState(initialCompanyId);
  const [granularity, setGranularity] = useState<"quarter" | "month">("quarter");
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState<number>(Math.ceil((new Date().getMonth() + 1) / 3));
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handle(e: FormEvent) {
    e.preventDefault();
    if (!companyId) {
      toast.error(t("forecasts.error.pickCustomer", "Pick a customer first."));
      return;
    }
    setSubmitting(true);
    try {
      const created = await forecastsApi.create({
        companyId,
        periodYear: year,
        periodQuarter: granularity === "quarter" ? quarter : undefined,
        periodMonth: granularity === "month" ? month : undefined,
        forecastLines: [],
        notes: notes || undefined,
      });
      toast.success(t("forecasts.toast.created", { period: periodLabel(created), defaultValue: `Created ${periodLabel(created)}.` }));
      await onSaved();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: 0, fontSize: 18, color: "var(--ink)" }}>{t("forecasts.modal.create.title", "New forecast")}</h2>
      <form onSubmit={handle} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {isInternal && (
          <Field label={t("forecasts.field.customer", "Customer")}>
            <Select<string>
              value={companyId}
              onChange={setCompanyId}
              options={[
                { value: "", label: t("forecasts.field.pickCustomer", "— Pick a customer —") },
                ...companies.map((c) => ({
                  value: c.id,
                  label: `${c.country ? `${flagEmoji(c.country)} ` : ""}${c.name}`
                    + `${c.country ? ` (${COUNTRIES.find((x) => x.code === c.country)?.name ?? c.country})` : ""}`,
                })),
              ]}
            />
          </Field>
        )}

        <Field label={t("forecasts.field.granularity", "Granularity")}>
          <Select<"quarter" | "month">
            value={granularity}
            onChange={setGranularity}
            options={[
              { value: "quarter", label: t("forecasts.field.granularity.quarter", "Quarter") },
              { value: "month", label: t("forecasts.field.granularity.month", "Month") },
            ]}
          />
        </Field>
        <div style={{ display: "flex", gap: 12 }}>
          <Field label={t("forecasts.field.year", "Year")}>
            <input type="number" className="input" value={year} min={2024} max={2100}
                   onWheel={(e) => e.currentTarget.blur()}
                   onChange={(e) => setYear(Number(e.target.value))} />
          </Field>
          {granularity === "quarter" ? (
            <Field label={t("forecasts.field.quarter", "Quarter")}>
              <Select<string>
                value={String(quarter)}
                onChange={(v) => setQuarter(Number(v))}
                options={QUARTERS.map((q) => ({ value: String(q), label: `Q${q}` }))}
              />
            </Field>
          ) : (
            <Field label={t("forecasts.field.month", "Month")}>
              <Select<string>
                value={String(month)}
                onChange={(v) => setMonth(Number(v))}
                options={MONTHS.map((m) => ({ value: String(m), label: String(m) }))}
              />
            </Field>
          )}
        </div>
        <Field label={t("forecasts.field.notesOptional", "Notes (optional)")}>
          <textarea
            className="input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{ resize: "vertical", fontFamily: "inherit" }}
          />
        </Field>
        <Actions onClose={onClose} submitting={submitting} />
      </form>
    </Modal>
  );
}

function EditModal({
  forecast, canEdit, onClose, onSaved,
}: {
  forecast: Forecast;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [lines, setLines] = useState<ForecastLine[]>(forecast.forecastLines ?? []);
  const [notes, setNotes] = useState(forecast.notes ?? "");
  const [products, setProducts] = useState<ProductSummary[]>([]);
  // Typeahead state mirrors OrderEditorPage's pattern — search by SKU/name,
  // pick from autocomplete; a full select dropdown does not scale with the catalog.
  const [productSearch, setProductSearch] = useState("");
  const [pickedProduct, setPickedProduct] = useState<ProductSummary | null>(null);
  const [quantity, setQuantity] = useState<number | "">(1);
  const [unit, setUnit] = useState("pcs");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void catalogApi
      .listProducts({ status: "PUBLISHED", page: 0, size: 2000, locale: "en" })
      .then((p) => setProducts(p.content.filter(Boolean)))
      .catch(() => undefined);
  }, []);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (q.length === 0) return products.slice(0, 8);
    return products
      .filter((p) =>
        `${p.sku} ${p.name ?? ""}`.toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [products, productSearch]);

  function resetPicker() {
    setPickedProduct(null);
    setProductSearch("");
    setQuantity(1);
  }

  function addLine() {
    if (!quantity || quantity <= 0) {
      toast.error(t("forecasts.error.qty", "Quantity must be greater than 0."));
      return;
    }
    // Catalog-only lines: no free-text escape hatch —
    // the server refuses product-less lines with FORECAST_LINE_PRODUCT_REQUIRED.
    if (!pickedProduct) {
      toast.error(t("forecasts.error.productRequired", "Pick a product from the catalog."));
      return;
    }
    setLines((prev) => [...prev, {
      productId: pickedProduct.id,
      freeText: null,
      quantity: Number(quantity),
      unit,
    }]);
    resetPicker();
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSubmitting(true);
    try {
      await forecastsApi.update(forecast.id, { forecastLines: lines, notes });
      toast.success(t("forecasts.toast.saved", "Saved."));
      await onSaved();
    } catch (err) {
      const e = err as ApiError;
      toast.error(e.code === "FORECAST_LINE_PRODUCT_REQUIRED" || e.code === "FORECAST_LINE_PRODUCT_NOT_FOUND"
        ? t("forecasts.error.productRequired", "Pick a product from the catalog.")
        : e.message);
    } finally {
      setSubmitting(false);
    }
  }

  function productLabel(productId?: string | null) {
    if (!productId) return null;
    const p = products.find((x) => x.id === productId);
    return p ? `${p.sku} · ${p.name}` : productId;
  }

  return (
    <Modal onClose={onClose}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--ink)" }}>
          {periodLabel(forecast)}
        </h2>
        <StatusBadge status={forecast.status} />
      </header>
      <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>{forecast.companyName}</p>

      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <section>
          <h3 style={subTitle}>{t("forecasts.lines.title", "Lines")}</h3>
          {lines.length === 0 ? (
            <p style={{ color: "var(--ink-4)", fontSize: 13 }}>{t("forecasts.lines.empty", "No lines yet — add some below.")}</p>
          ) : (
            <table style={miniTbl}>
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>{t("forecasts.lines.col.item", "Item")}</th>
                  <th style={th}>{t("forecasts.lines.col.qty", "Qty")}</th>
                  <th style={th}>{t("forecasts.lines.col.unit", "Unit")}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td style={td}>{i + 1}</td>
                    <td style={td}>{productLabel(l.productId) ?? l.freeText ?? "—"}</td>
                    <td style={td}>{l.quantity}</td>
                    <td style={td}>{l.unit}</td>
                    <td style={td}>
                      {canEdit && (
                        <button type="button" onClick={() => removeLine(i)} style={dangerLink}>{t("forecasts.lines.remove", "Remove")}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {canEdit && (
            <div className="addline-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 100px 90px auto", gap: 8, marginTop: 12, alignItems: "end" }}>
              <Field label={t("forecasts.field.product", "Product")}>
                <div style={{ position: "relative" }}>
                  <input
                    className="input"
                    placeholder={t("forecasts.product.searchPlaceholder", "Search SKU or name — e.g. BIOPRO 15")}
                    value={pickedProduct
                      ? `${pickedProduct.sku} · ${pickedProduct.name}`
                      : productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setPickedProduct(null);
                    }}
                    style={{ width: "100%" }}
                  />
                  {!pickedProduct && productSearch.trim().length > 0 && filteredProducts.length > 0 && (
                    <div className="dd-scroll" style={typeaheadPanel}>
                      {filteredProducts.map((p) => (
                        <button
                          type="button"
                          key={p.id}
                          onClick={() => { setPickedProduct(p); setProductSearch(""); }}
                          style={typeaheadOption}
                        >
                          <span style={typeaheadSku}>{p.sku}</span>
                          <span style={{ flex: 1 }}>{p.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {!pickedProduct && productSearch.trim().length > 0 && filteredProducts.length === 0 && (
                    <div className="dd-scroll" style={typeaheadPanel}>
                      <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--ink-3)" }}>
                        {t("forecasts.product.noMatch", "No match — forecast lines must reference a catalog product.")}
                      </div>
                    </div>
                  )}
                </div>
              </Field>
              <Field label={t("forecasts.field.qty", "Qty")}>
                <input className="input" type="number" min={0} step="0.001" value={quantity}
                       onWheel={(e) => e.currentTarget.blur()}
                       onChange={(e) => setQuantity(e.target.value ? Number(e.target.value) : "")} />
              </Field>
              <Field label={t("forecasts.field.unit", "Unit")}>
                <input className="input" value={unit} onChange={(e) => setUnit(e.target.value)} />
              </Field>
              <button type="button" onClick={addLine} className="btn primary">
                {t("forecasts.lines.add", "Add")}
              </button>
            </div>
          )}
        </section>

        <Field label={t("forecasts.field.notes", "Notes")}>
          <textarea
            className="input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canEdit}
            rows={3}
            style={{ resize: "vertical", fontFamily: "inherit" }}
          />
        </Field>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} className="btn">
            {t("forecasts.action.close", "Close")}
          </button>
          {canEdit && (
            <button type="submit" disabled={submitting} className="btn primary">
              {submitting ? t("common.saving", "Saving…") : t("forecasts.action.save", "Save")}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}

function StatusBadge({ status }: { status: ForecastStatus }) {
  const { t } = useTranslation();
  const palette: Record<ForecastStatus, { bg: string; fg: string }> = {
    DRAFT:     { bg: "var(--surface-muted)", fg: "var(--ink-3)" },
    SUBMITTED: { bg: "var(--info-bg)", fg: "var(--info)" },
  };
  const labels: Record<ForecastStatus, string> = {
    DRAFT:     t("forecasts.status.DRAFT", "DRAFT"),
    SUBMITTED: t("forecasts.status.SUBMITTED", "SUBMITTED"),
  };
  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  const c = palette[status] ?? { bg: "var(--surface-2)", fg: "var(--ink-2)" };
  return <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{labels[status]}</span>;
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={{ color: "var(--ink-3)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
      {children}
    </label>
  );
}

function Actions({ onClose, submitting }: { onClose: () => void; submitting: boolean }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
      <button type="button" onClick={onClose} className="btn">{t("action.cancel", "Cancel")}</button>
      <button type="submit" disabled={submitting} className="btn primary">
        {submitting ? t("common.saving", "Saving…") : t("forecasts.action.save", "Save")}
      </button>
    </div>
  );
}

const kpiGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 };
const chipBar: React.CSSProperties = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: React.CSSProperties = { textAlign: "left", padding: "11px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "12px", borderBottom: "1px solid var(--border)" };
const miniTbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 8 };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-primary)", cursor: "pointer", marginLeft: 8, fontSize: 13 };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", marginLeft: 8, fontSize: 13 };
const subTitle: React.CSSProperties = { margin: "0 0 6px", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 760, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14, maxHeight: "90vh", overflowY: "auto" };
const emptyCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
const typeaheadPanel: React.CSSProperties = {
  position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", zIndex: 60,
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6,
  boxShadow: "var(--shadow-2)", maxHeight: 280, overflowY: "auto",
  display: "flex", flexDirection: "column", padding: 4, gap: 2,
};
const typeaheadOption: React.CSSProperties = {
  display: "flex", gap: 10, alignItems: "center",
  padding: "8px 10px", background: "transparent", border: "none",
  cursor: "pointer", textAlign: "left", borderRadius: 4, fontSize: 13, color: "var(--ink)",
};
const typeaheadSku: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 11, color: "var(--ink-4)", minWidth: 80,
};
